import React, { useMemo, useState, useEffect } from "react";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { Line } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import ViewMoreIconButton from "./ViewMoreIconButton";
import { Activity, TrendingUp, ArrowUpLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);
ChartJS.defaults.font.family = "yekan, Vazir, sans-serif";

/* ================================================================
 *  Helpers
 * ================================================================ */
const toPersianNumber = (num) =>
  String(num).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);

export default function HourlyMsgCountChart({ messagePerHourChart }) {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = (i18n.language === "en");
  const [isMobile, setIsMobile] = useState(false);

  /* ---------- responsive ---------- */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* ---------- داده ---------- */
  const rawData = useMemo(() => {
    if (!Array.isArray(messagePerHourChart)) return [];
    return messagePerHourChart.map((item) => {
      const hour = item.hour ?? 0;
      const jalaliDate = new DateObject({
        date: item.date,
        calendar: "gregorian",
        locale: persian_fa,
      })
        .convert(persian)
        .format("YYYY/MM/DD");

      return {
        ...item,
        date: jalaliDate,
        displayLabel: `${toPersianNumber(hour)} - ${toPersianNumber(jalaliDate)}`,
        hour,
        count: item.count ?? 0,
      };
    });
  }, [messagePerHourChart]);

  const labels = useMemo(() => rawData.map((i) => i.displayLabel), [rawData]);
  const values = useMemo(() => rawData.map((i) => i.count), [rawData]);
  const navigate = useNavigate();
  /* ---------- Empty / Loading ---------- */
  if (!messagePerHourChart || messagePerHourChart.length === 0) {
    return (
      <div
        className={`
          relative w-full h-[500px] overflow-hidden
          rounded-2xl
          bg-gradient-to-br from-slate-100 via-teal-50 to-teal-100/70
          dark:!bg-gradient-to-br dark:!from-slate-900 dark:!via-[#0B2E2F] dark:!to-slate-900
          backdrop-blur-xl
          border border-teal-500/20 dark:!border-teal-400/15
          shadow-[0_10px_40px_-20px_rgba(15,118,110,0.45)]
          dark:!shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)]
          flex flex-col items-center justify-center gap-3
          
        `}
      >
        <div className="pointer-events-none absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
        <div className="h-14 w-14 rounded-full border-2 border-dashed border-teal-400/40 flex items-center justify-center">
          <TrendingUp size={22} className="animate-pulse text-teal-400/70" />
        </div>
        <p className="text-sm animate-pulse text-slate-500 dark:!text-slate-400">
          {messagePerHourChart && messagePerHourChart.length === 0
            ? t("hourlyMsgChart.noData")
            : t("hourlyMsgChart.loading")}
        </p>
      </div>
    );
  }

  /* ================================================================
   *  Chart.js config
   * ================================================================ */
  const data = {
    labels,
    datasets: [
      {
        label: t("hourlyMsgChart.datasetLabel"),
        data: values,
        borderColor: "#14B8A6",
        backgroundColor: (ctx) => {
          const chart = ctx.chart;
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return "rgba(20,184,166,0.15)";
          const gradient = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, "rgba(20,184,166,0.45)");
          gradient.addColorStop(0.5, "rgba(251,191,36,0.20)");
          gradient.addColorStop(1, "rgba(251,191,36,0.02)");
          return gradient;
        },
        tension: 0.4,
        fill: true,
        pointRadius: 3.5,
        pointHoverRadius: 6,
        pointBackgroundColor: "#FCD34D",
        pointBorderColor: "#0F766E",
        pointBorderWidth: 2,
        pointHoverBackgroundColor: "#FBBF24",
        pointHoverBorderColor: "#14B8A6",
        borderWidth: 3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      zoom: {
        pan: { enabled: true, mode: "x" },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: "x",
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(15, 23, 42, 0.95)",
        titleColor: "#FCD34D",
        bodyColor: "#5EEAD4",
        titleFont: { family: "yekan, Vazir, sans-serif", size: 13, weight: "700" },
        bodyFont: { family: "yekan, Vazir, sans-serif", size: 13, weight: "700" },
        padding: 12,
        cornerRadius: 12,
        borderColor: "rgba(251, 191, 36, 0.4)",
        borderWidth: 1,
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            return rawData[index].displayLabel;
          },
          label: (tooltipItem) =>
            `${tooltipItem.dataset.label}: ${toPersianNumber(
              tooltipItem.formattedValue
            )} ${t("hourlyMsgChart.messageUnit")}`,
        },
      },
      title: { display: false },
    },
    scales: {
      x: {
        title: {
          display: !isMobile,
          text: t("hourlyMsgChart.xAxisTitle"),
          color: "#64748B",
          font: { size: 12, weight: "600", family: "yekan, Vazir, sans-serif" },
          padding: { top: 8 },
        },
        ticks: {
          color: "#64748B",
          font: { size: 11, family: "yekan, Vazir, sans-serif" },
          callback: (value, index) => labels[index],
          maxRotation: 60,
          minRotation: 45,
          autoSkip: true,
          maxTicksLimit: isMobile ? 5 : 12,
        },
        grid: { display: false },
        border: { color: "rgba(20, 184, 166, 0.15)" },
      },
      y: {
        title: {
          display: !isMobile,
          text: t("hourlyMsgChart.yAxisTitle"),
          color: "#64748B",
          font: { size: 12, weight: "600", family: "yekan, Vazir, sans-serif" },
          padding: { bottom: 8 },
        },
        ticks: {
          callback: (value) => toPersianNumber(value),
          color: "#64748B",
          font: { size: 11, family: "yekan, Vazir, sans-serif" },
          padding: 6,
        },
        grid: {
          color: "rgba(20, 184, 166, 0.12)",
          drawTicks: false,
        },
        border: { display: false },
      },
    },
  };

  /* ================================================================
   *  RENDER
   * ================================================================ */
  return (
    <div
      className={`
        relative w-full h-[500px] overflow-hidden
        rounded-2xl
        bg-gradient-to-br from-slate-100 via-teal-50 to-teal-100/70
        dark:!bg-gradient-to-br dark:!from-slate-900 dark:!via-[#0B2E2F] dark:!to-slate-900
        backdrop-blur-xl
        border border-teal-500/20 dark:!border-teal-400/15
        shadow-[0_10px_40px_-20px_rgba(15,118,110,0.45)]
        dark:!shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)]
        transition-colors duration-500
        flex flex-col
        ${isEnglish && 'font-sans'}
      `}
    >
      {/* خط تزئینی طلایی بالا */}
      <div className="pointer-events-none absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />

      {/* گلوی طلایی */}
      <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-300/15 blur-3xl dark:!bg-amber-400/10" />

      {/* گلوی teal */}
      <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-teal-400/15 blur-3xl dark:!bg-teal-500/10" />

      {/* Header */}
      <div className="relative flex items-center justify-between gap-3 p-4 sm:p-5 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="
              shrink-0 flex h-8 w-8 items-center justify-center rounded-lg
              bg-gradient-to-br from-amber-300/30 to-teal-400/20
              border border-amber-300/40
            "
          >
            <Activity size={15} className="text-amber-400" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800 dark:!text-amber-100 truncate">
              {t("hourlyMsgChart.title")}
            </h3>
            <p className="text-[11px] text-slate-500 dark:!text-slate-400 truncate">
              {t("hourlyMsgChart.subtitle")}
            </p>
          </div>
        </div>

        <div className="shrink-0">
          <button
            type="button"
            onClick={() => navigate("/user/message-per-hour")}
            className="
    group flex h-9 w-9 items-center justify-center
    rounded-xl
    border border-teal-500/20
    bg-white/40
    text-teal-600
    backdrop-blur-md
    transition-all duration-300
    hover:border-amber-400/40
    hover:bg-amber-400/10
    hover:text-amber-500
    dark:!border-teal-400/20
    dark:!bg-white/5
    dark:!text-teal-300
    dark:hover:!border-amber-400/40
    dark:hover:!bg-amber-400/10
    dark:hover:!text-amber-300
  "
            title={t("hourlyMsgChart.viewFullChart")}
          >
            <ArrowUpLeft
              size={18}
              strokeWidth={2}
              className="transition-transform duration-300 group-hover:-translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className={`relative flex-1 min-h-0 px-2 sm:px-4 pb-3 sm:pb-4 ${isEnglish && 'font-sans'}`}>
        <Line data={data} options={options} />
      </div>
    </div>
  );
}